// src/components/ProfileInfo/ProfileEdit.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, updateProfile } from "../../APIs/ProfileApi";

const EditProfile = () => {
  const navigate = useNavigate();
  const [profileId, setProfileId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [userName, setUserName] = useState("");
  const [banner, setBanner] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const bannerRef = useRef();
  const avatarRef = useRef();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getCurrentUser();
        const me = res.data ?? res;
        setProfileId(me.profile_id);
        setFirstName(me.first_name || "");
        setSurname(me.last_name || "");
        setUserName(me.username || "");
        setBannerPreview(me.profile?.banner || null);
        setAvatarPreview(me.profile?.avatar || null);
      } catch {
        // leave form empty — user can still fill in fields
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) { setBanner(file); setBannerPreview(URL.createObjectURL(file)); }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) { setAvatar(file); setAvatarPreview(URL.createObjectURL(file)); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!profileId) return;
    const formData = new FormData();
    formData.append("first_name", firstName);
    formData.append("last_name", surname);
    formData.append("username", userName);
    if (avatar) formData.append("avatar", avatar);
    if (banner) formData.append("banner", banner);

    try {
      await updateProfile(profileId, formData);
      navigate(`/profile/${userName}`);
    } catch {
      alert("Error updating profile");
    }
  };

  if (loading) return <div className="text-center text-white mt-20">Loading...</div>;

  return (
    <div className="min-h-screen bg-neutral-900 text-white flex flex-col items-center p-6 pb-40">
      {/* Banner */}
      <div className="relative w-full max-w-lg h-48 bg-neutral-800 rounded-xl overflow-hidden">
        {bannerPreview ? (
          <img src={bannerPreview} alt="Banner" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500">No banner</div>
        )}
        <button
          type="button"
          onClick={() => bannerRef.current.click()}
          className="absolute bottom-3 right-3 bg-gray-700 px-3 py-1 rounded-lg text-sm hover:bg-gray-600"
        >
          Change Banner
        </button>
        <input type="file" ref={bannerRef} onChange={handleBannerChange} className="hidden" accept="image/*" />
      </div>

      {/* Avatar */}
      <div className="relative -mt-12">
        <img
          src={avatarPreview || "/Images/default-avatar.svg"}
          alt="Avatar"
          className="w-24 h-24 rounded-full border-4 border-neutral-900 object-cover"
        />
        <button
          type="button"
          onClick={() => avatarRef.current.click()}
          className="absolute bottom-0 right-0 bg-gray-700 px-2 py-1 text-xs rounded-md hover:bg-gray-600"
        >
          Edit
        </button>
        <input type="file" ref={avatarRef} onChange={handleAvatarChange} className="hidden" accept="image/*" />
      </div>

      {/* Form */}
      <form id="edit-profile-form" onSubmit={handleSubmit} className="w-full max-w-lg bg-neutral-800 p-6 mt-4 rounded-2xl shadow-lg space-y-4">
        <div>
          <label className="block text-sm mb-1">First Name</label>
          <input
            type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
            className="w-full bg-neutral-900 rounded-lg p-2 border border-neutral-700 focus:outline-none focus:border-blue-500"
            placeholder="Your first name"
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Surname</label>
          <input
            type="text" value={surname} onChange={(e) => setSurname(e.target.value)}
            className="w-full bg-neutral-900 rounded-lg p-2 border border-neutral-700 focus:outline-none focus:border-blue-500"
            placeholder="Your surname"
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Username</label>
          <input
            type="text" value={userName} onChange={(e) => setUserName(e.target.value)}
            className="w-full bg-neutral-900 rounded-lg p-2 border border-neutral-700 focus:outline-none focus:border-blue-500"
            placeholder="Choose your username"
          />
        </div>
        <button type="submit" className="hidden md:block w-full bg-blue-600 hover:bg-blue-700 rounded-lg py-2 font-semibold">
          Save Changes
        </button>
      </form>

      {/* Mobile-only: fixed above footer, safe-area-aware */}
      <button
        type="submit"
        form="edit-profile-form"
        className="md:hidden fixed left-4 right-4 z-[60] bg-blue-600 hover:bg-blue-700 rounded-lg py-2 font-semibold text-white"
        style={{ bottom: 'calc(90px + env(safe-area-inset-bottom))' }}
      >
        Save Changes
      </button>
    </div>
  );
};

export default EditProfile;
